import { createSelector } from 'reselect';
import { selectors as userSelectors } from '../user';

/**
 * Returns the complete organization state
 *
 * @param {object} state - Current state
 * @returns {object} - The entire organization state
 */
export const getOrgState = (state) => state.org;

/**
 * Returns whether the user has completed the billing process
 *
 * @param {object} state - Current state
 * @returns {bool} - Returns whether the user has completed the billing process
 */
export const needsBilling = (state) => !state.org.billingSetupComplete;

/**
 * Returns whether a user needs to complete any of the sign up steps
 *
 * @param {object} state - Current state
 * @returns {boolean} - Whether the user needs to complete any of the sign up steps
 */
export const needsSignUpSteps = createSelector(
  userSelectors.needsEmailVerification,
  userSelectors.needsOrganization,
  (email, org) => email || org,
);

/**
 * Returns whether or not an ORG is AWS-only.
 *
 * @param {object} state - Current state
 * @returns {boolean} - Whether or not the org is AWS-only
 */
export const awsOnlySelector = createSelector(getOrgState, (orgState) =>
  Boolean(orgState.awsCustomer),
);

/**
 * Returns the org-level ORv4ID, if it exists
 *
 * @param {object} state - Current state
 * @returns {(string|null)} - The org's ORv4ID, or null if there is none
 */
export const orgORv4IDSelector = createSelector(getOrgState, (orgState) => orgState.orv4id);

/**
 * Returns whether or not an ORG is fastlane billed.
 * In APIV, BILLING_FASTLANE = 4.
 *
 * @param {object} state - Current state
 * @returns {boolean} - Whether or not the org is fastlane billed
 */
export const isFastlaneCustomerSelector = createSelector(
  getOrgState,
  (orgState) => orgState.billingType === 4,
);
